import { useCallback, useRef } from 'react';
import { convertToSixDigitHex, EMPTY_CHIP_COLOR, recentStandardColors } from '.';
import { createUseHtmlId } from '../../../hooks/useHtmlId';
import { useIsRTL } from '../../../locale';
import { useNCTextLocalizer } from '../../../locale/useNCTextLocalizer';
import { postfixTestId } from '../../../util/testId';
import { Label } from '../../Label';
import { Flow } from '../../Layouts/Flow';
import { basicColors } from './BasicColorPallete';
import { ColorChips } from './ColorChips';
import { ColorIndicator } from './ColorIndicator';
import cn from './BasicColorPicker.css.json';
import './BasicColorPicker.css';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useHtmlIds = createUseHtmlId('BasicColorPicker');
export const BasicColorPicker = props => {
  const localize = useNCTextLocalizer('ColorPicker');
  const standardLabel = localize('standard.label');
  const automaticLabel = localize('automatic.label');
  const nocolorLabel = localize('noColor.label');
  const recentCustomLabel = localize('recentCustom.label');
  const recentStandardLabel = localize('recentStandard.label');
  const isRTL = useIsRTL();
  const numberOfRecentCustomRows = Math.max(0, Math.min(props.numberOfRecentCustomRows ?? 1, 3));
  const recentCustomColorBlocks = [...props.recentCustomColors, ...Array(numberOfRecentCustomRows * 7).fill(EMPTY_CHIP_COLOR)].slice(0, numberOfRecentCustomRows * 7);
  const stdColorsContainerRef = useRef(null);
  const selectedColor = props.newColor;
  const automaticColor = convertToSixDigitHex(props.automaticColor ?? '#000000');
  const isEmptyChip = chip => {
    return chip.firstElementChild.style.backgroundColor === EMPTY_CHIP_COLOR;
  };
  const changeFocus = (focusedElement, elementToFocus) => {
    focusedElement.tabIndex = -1;
    elementToFocus.tabIndex = 0;
    elementToFocus.focus();
  };
  const onRecentStandardKeyDown = e => {
    const eventTarget = e.target;
    let preventDefault = true;
    switch (e.key) {
      case 'ArrowUp': /* falls through */
      case isRTL ? 'ArrowRight' : 'ArrowLeft':
        if (eventTarget.previousElementSibling) {
          const previous = eventTarget.previousElementSibling;
          if (isEmptyChip(previous)) break;
          changeFocus(eventTarget, previous);
        }
        break;
      case 'ArrowDown': /* falls through */
      case isRTL ? 'ArrowLeft' : 'ArrowRight':
        if (eventTarget.nextElementSibling) {
          const next = eventTarget.nextElementSibling;
          if (isEmptyChip(next)) break;
          changeFocus(eventTarget, next);
        }
        break;
      case 'Home':
        if (eventTarget.parentElement) {
          const children = [...eventTarget.parentElement.childNodes];
          changeFocus(eventTarget, children[0]);
        }
        break;
      case 'End':
        if (eventTarget.parentElement) {
          const lastColorIndex = recentStandardColors.filter(color => color !== EMPTY_CHIP_COLOR).length - 1;
          const children = [...eventTarget.parentElement.childNodes];
          changeFocus(eventTarget, children[lastColorIndex]);
        }
        break;
      default:
        preventDefault = false;
        break;
    }
    if (preventDefault) {
      e.preventDefault();
      e.stopPropagation();
    }
  };
  const onRecentCustomKeyDown = e => {
    if (numberOfRecentCustomRows === 1 && e.key !== 'End' && e.key !== 'Home') {
      onRecentStandardKeyDown(e);
    } else {
      let preventDefault = true;
      const eventTarget = e.target;
      const colorCount = props.recentCustomColors.length;
      const columnCount = 7;
      switch (e.key) {
        case isRTL ? 'ArrowRight' : 'ArrowLeft':
          if (eventTarget.previousElementSibling) {
            const previous = eventTarget.previousElementSibling;
            if (isEmptyChip(previous)) break;
            changeFocus(eventTarget, previous);
          }
          break;
        case isRTL ? 'ArrowLeft' : 'ArrowRight':
          if (eventTarget.nextElementSibling) {
            const next = eventTarget.nextElementSibling;
            if (isEmptyChip(next)) break;
            changeFocus(eventTarget, next);
          }
          break;
        case 'ArrowUp':
          if (eventTarget.parentElement) {
            const children = [...eventTarget.parentElement.childNodes];
            const currentIndex = children.indexOf(eventTarget);
            if (currentIndex === 0) break;
            const currentRow = Math.floor(currentIndex / columnCount);
            const currentColumn = currentIndex % columnCount;
            const lastChipRow = Math.floor((colorCount - 1) / columnCount);
            const lastChipColumn = (colorCount - 1) % columnCount;
            const nextIndex = currentRow === 0 ? currentColumn > lastChipColumn + 1 ? currentIndex + (lastChipRow - 1) * columnCount - 1 : currentIndex + lastChipRow * columnCount - 1 : currentIndex - columnCount;
            const next = children[nextIndex];
            if (isEmptyChip(next)) break;
            changeFocus(eventTarget, next);
          }
          break;
        case 'ArrowDown':
          if (eventTarget.parentElement) {
            const children = [...eventTarget.parentElement.childNodes];
            const currentIndex = children.indexOf(eventTarget);
            if (currentIndex === colorCount - colorCount % columnCount - 1) break;
            const currentColumn = currentIndex % columnCount;
            const nextIndex = currentIndex < colorCount - columnCount ? currentIndex + columnCount : currentColumn + 1;
            const next = children[nextIndex];
            if (isEmptyChip(next)) break;
            changeFocus(eventTarget, next);
          }
          break;
        case 'Home':
          if (eventTarget.parentElement) {
            const children = [...eventTarget.parentElement.childNodes];
            changeFocus(eventTarget, children[0]);
          }
          break;
        case 'End':
          if (eventTarget.parentElement) {
            const lastColorIndex = numberOfRecentCustomRows * columnCount >= colorCount ? colorCount - 1 : numberOfRecentCustomRows * columnCount - 1;
            const children = [...eventTarget.parentElement.childNodes];
            changeFocus(eventTarget, children[lastColorIndex]);
          }
          break;
        default:
          preventDefault = false;
          break;
      }
      if (preventDefault) {
        e.preventDefault();
        e.stopPropagation();
      }
    }
  };
  const handleColorChipPress = (color, type) => {
    props.onColorSelect?.({
      color,
      type
    });
  };
  const handleonkeydown = event => {
    let preventDefault = true;
    const eventTarget = event.target;
    const rowCount = 5;
    switch (event.key) {
      case isRTL ? 'ArrowRight' : 'ArrowLeft':
        {
          if (eventTarget.parentNode) {
            const children = eventTarget.parentNode.childNodes;
            const currentIndex = [...children].indexOf(eventTarget);
            if (currentIndex > 0) {
              const nextIndex = currentIndex >= rowCount ? currentIndex - rowCount : children.length - 1 - rowCount + currentIndex;
              const next = children[nextIndex];
              changeFocus(eventTarget, next);
            }
          }
        }
        break;
      case isRTL ? 'ArrowLeft' : 'ArrowRight':
        {
          if (eventTarget.parentNode) {
            const children = eventTarget.parentNode.childNodes;
            const currentIndex = [...children].indexOf(eventTarget);
            if (currentIndex < children.length - 1) {
              const nextIndex = currentIndex < children.length - rowCount ? currentIndex + rowCount : rowCount - (children.length - 1 - currentIndex);
              const next = children[nextIndex];
              changeFocus(eventTarget, next);
            }
          }
        }
        break;
      case 'ArrowUp':
        {
          if (eventTarget.previousElementSibling) {
            const next = eventTarget.previousElementSibling;
            changeFocus(eventTarget, next);
          }
        }
        break;
      case 'ArrowDown':
        {
          if (eventTarget.nextElementSibling) {
            const next = eventTarget.nextElementSibling;
            changeFocus(eventTarget, next);
          }
        }
        break;
      case 'Home':
        if (eventTarget.parentNode?.firstElementChild) {
          const next = eventTarget.parentNode.firstElementChild;
          changeFocus(eventTarget, next);
        }
        break;
      case 'End':
        if (eventTarget.parentNode?.lastElementChild) {
          const next = eventTarget.parentNode.lastElementChild;
          changeFocus(eventTarget, next);
        }
        break;
      default:
        preventDefault = false;
        break;
    }
    if (preventDefault) {
      event.stopPropagation();
      event.preventDefault();
    }
  };
  const [standardColorsId, recentCustomColorsId, recentStandardColorsId] = useHtmlIds(3);
  const setContainerMaxWidth = useCallback(element => {
    if (element && stdColorsContainerRef.current) {
      element.style.maxWidth = `${stdColorsContainerRef.current.offsetWidth}px`;
    }
  }, []);
  return /*#__PURE__*/_jsxs(Flow, {
    id: props.id,
    role: "tabpanel",
    orientation: "vertical",
    className: cn.container,
    "data-testid": postfixTestId(props.testId, 'container'),
    children: [props.showColorIndicator && /*#__PURE__*/_jsx(ColorIndicator, {
      currentColor: props.selectedColor,
      newColor: props.newColor
    }), /*#__PURE__*/_jsxs(Flow, {
      ref: setContainerMaxWidth,
      orientation: "vertical",
      spacing: "s",
      className: cn.basicColorContainer,
      children: [/*#__PURE__*/_jsxs(Flow, {
        orientation: "vertical",
        spacing: "xs",
        children: [/*#__PURE__*/_jsx(Label, {
          id: standardColorsId,
          className: cn.label,
          children: standardLabel
        }), /*#__PURE__*/_jsx(Flow, {
          ref: stdColorsContainerRef,
          role: "toolbar",
          "aria-labelledby": standardColorsId,
          className: cn.standardColorChips,
          onKeyDown: handleonkeydown,
          spacing: "none",
          children: basicColors.map(standardColor => {
            return /*#__PURE__*/_jsx(ColorChips, {
              ...props,
              color: standardColor,
              type: "standard",
              selected: standardColor === selectedColor?.color && selectedColor?.type === 'standard' || standardColor === '#000000' && selectedColor?.type !== 'standard',
              tabToStandardBlock: standardColor === '#000000' && selectedColor?.type !== 'standard',
              onPress: handleColorChipPress,
              testId: postfixTestId(props.testId, 'standardColorChip')
            }, standardColor);
          })
        })]
      }), numberOfRecentCustomRows !== 0 && /*#__PURE__*/_jsxs(Flow, {
        orientation: "vertical",
        spacing: "xs",
        children: [/*#__PURE__*/_jsx(Label, {
          id: recentCustomColorsId,
          className: cn.label,
          children: recentCustomLabel
        }), /*#__PURE__*/_jsx(Flow, {
          role: "toolbar",
          "aria-labelledby": recentCustomColorsId,
          className: cn.recentCustomChips,
          onKeyDown: onRecentCustomKeyDown,
          children: recentCustomColorBlocks.map((color, i) => {
            return /*#__PURE__*/_jsx(ColorChips, {
              testId: postfixTestId(props.testId, `recentCustomColorChip-${i}`),
              color: color,
              type: "custom",
              onPress: handleColorChipPress,
              focusable: i === 0 && color !== EMPTY_CHIP_COLOR
            }, `recent-custom-${i}`);
          })
        })]
      }), /*#__PURE__*/_jsxs(Flow, {
        orientation: "vertical",
        spacing: "xs",
        children: [/*#__PURE__*/_jsx(Label, {
          id: recentStandardColorsId,
          className: cn.label,
          children: recentStandardLabel
        }), /*#__PURE__*/_jsx(Flow, {
          role: "toolbar",
          "aria-labelledby": recentStandardColorsId,
          className: cn.recentStandardChips,
          onKeyDown: onRecentStandardKeyDown,
          children: recentStandardColors.map((color, i) => {
            return /*#__PURE__*/_jsx(ColorChips, {
              testId: postfixTestId(props.testId, `recentStandardColorChip-${i}`),
              color: color,
              type: "standard",
              onPress: handleColorChipPress,
              focusable: i === 0 && color !== EMPTY_CHIP_COLOR
            }, `recent-standard-${i}`);
          })
        })]
      }), /*#__PURE__*/_jsxs(Flow, {
        spacing: "s",
        orientation: "vertical",
        className: cn.defultColorSelection,
        onKeyDown: e => {
          if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
            e.stopPropagation();
            e.preventDefault();
          }
        },
        children: [!props.hideAutomaticColor && /*#__PURE__*/_jsxs(Flow, {
          spacing: "xs",
          children: [/*#__PURE__*/_jsx(ColorChips, {
            type: "automatic",
            ...props,
            color: automaticColor,
            selected: automaticColor.toUpperCase() === selectedColor?.color.toUpperCase() && selectedColor?.type === 'automatic',
            onPress: handleColorChipPress,
            testId: postfixTestId(props.testId, 'automaticColorChip')
          }), /*#__PURE__*/_jsx(Label, {
            wrapText: false,
            children: automaticLabel
          })]
        }), !props.hideNoColor ? /*#__PURE__*/_jsxs(Flow, {
          spacing: "xs",
          children: [/*#__PURE__*/_jsx(ColorChips, {
            type: "nocolor",
            ...props,
            color: "",
            selected: selectedColor?.color === '' && selectedColor?.type === 'nocolor',
            onPress: handleColorChipPress,
            testId: postfixTestId(props.testId, 'nocolorColorChip')
          }), /*#__PURE__*/_jsx(Label, {
            wrapText: false,
            children: nocolorLabel
          })]
        }) : null]
      })]
    })]
  });
};
//# sourceMappingURL=BasicColorPicker.js.map